import { LitElement, html, css, PropertyValues } from 'lit';
import {property, customElement} from 'lit/decorators.js';
import { SvgIcons } from '../../voyage-svg-icons/src/voyage-svg-icons';
import { VesselCharacteristics, VesselPlanningModel, VoyagePlan } from '../../../src/models/vessel-planning-model';
import { VoyagePassagePlanPdf } from './voyage-passage-plan-pdf';
import '../../voyage-popup/src/voyage-popup';
import './voyage-passage-plan-general';
import './voyage-passage-plan-voyage';
import './voyage-passage-plan-overview/voyage-passage-plan-overview';
import './voyage-passage-plan-text-content';
import { DataExtractor } from './data-extractor';
import { Content } from './voyage-passage-plan-text-content';
import { RoutePassagePlanProperties, VoyageRoute } from '../../../src/models/voyage-routing-model';
import { VoyageImoModel } from '../../../src/models/voyage-imo-model';
import { IndexedDBService } from '../../../indexed-db/services/db_service';
import { ScheduleEntry } from '../../../src/models/types/voyage-schedules-model.types';
import { deepCopy, isOnline } from '../../../src/utils/helpers';
import { PassagePlanEvents, SuggestedRouteEvents } from '../../voyage-suggested-routes/src/types';
import { AlignBPREvent } from './voyage-passage-plan-overview/type';
import { ShowToastEvent } from '../../voy-toast/src/voy-toast';
import { WRMessageTypes } from '../../../src/services/types/vessel-notifications-services.types';
import { VoyageToastManager } from '../../voy-toast/src/voy-toast-manager';

const templateData = [
    {
        id: "reminders",
        title: "Reminders",
    },
    {
        id: "gpsEcdisRadarMap",
        title: "GPS/ECDIS/Radar map",
    },
    {
        id: "booksAndTables",
        title: "Books & Tables",
    },
    {
        id: "tideTables",
        title: "Tide tables / program",
    },
    {
        id: "chartsAndLightsNotes",
        title: "Charts & Lights Notes",
    },
    {
        id: "publicationsNotes",
        title: "Publications Notes",
    },
    {
        id: "listOfRadioSignals",
        title: "List of radio signals for voyage",
    },
    {
        id: "info",
        title: "Weather information",
    },
    {
        id: "operationalCondition",
        title: "Operational condition",
    },
    {
        id: "listOfLights",
        title: "List of lights",
    },
    {
        id: "failures",
        title: "List of failures",
    },
    {
        id: "mastersOrders",
        title: "Master’s orders for this passage",
    }
]

const isVesselOnCloud = (<any>window)["appenv"].vesselOnCloud;
@customElement('voyage-passage-plan')
export class VoyagePassagePlan extends LitElement {
    @property({ type: Boolean }) shown: boolean = false;
    @property({ attribute: false }) nonHydraCalculatedRoute: VoyageRoute|null = null;
    @property({ type: Boolean }) _showPassagePlanTextContent: boolean = false;
    @property({ type: String }) _fileTitle: string = '';
    @property({ type: String, attribute: "schedule-id" }) scheduleId: string = "";
    @property({ type: String }) state: string = "General";
    @property({ type: Boolean }) readOnly: boolean = false;
    @property({ type: String })  showInFloatPanel: string = "";

    @property({ attribute: false }) private _plan: VoyagePlan | null = null;
    @property({ attribute: false }) private _data: Content[] = [];
    @property({ attribute: false }) private _offlinePlan: VoyagePlan|null = null;
    @property({attribute: false}) private _vesselCharacteristics: VesselCharacteristics|null = null;
    @property({attribute: false}) private _cannotContinue: boolean = false;
    @property({attribute: false}) private _showSaveAsDraftButton: boolean = false;
    @property({ type: Function }) toggleFuelEfficientModal: (isVisible: boolean) => void = () => {};
    @property({attribute: false}) private _loading: boolean = isOnline() ? true : false;
    @property({attribute: false}) private _currentSchedule: ScheduleEntry | null = null;
    private _shownToasts: Set<string> = new Set();
    
    
    private _showLoader: boolean = false;

    constructor() {
        super();
        
        this._onPlanUpdated = this._onPlanUpdated.bind(this);
        this._closeVoyTextContentPopup = this._closeVoyTextContentPopup.bind(this);
        this._updatePassagePlan = this._updatePassagePlan.bind(this);
        this._exportTemplate = this._exportTemplate.bind(this);
        this._saveAndClose = this._saveAndClose.bind(this);
        this._close = this._close.bind(this);
        this._setShowSaveAsDraftButton = this._setShowSaveAsDraftButton.bind(this);
        this._save = this._save.bind(this);
        this._loading = false;
        this._showLoaderFn = this._showLoaderFn.bind(this);
        this._hideLoader = this._hideLoader.bind(this);
    }

    get showLoader() {
        return this._showLoader;
    }

    set showLoader(value: boolean) {
        this._showLoader = value;
    }


    static get styles() {
        return [
            css`
            :host {
                font-family: MaerskText;
            }

            button {
                cursor: pointer;
            }

            .headerTextContainer {
                display: flex;
                align-items: center;
                background: var(--neutrals-grey-09);
                border-bottom: 1px solid var(--neutrals-grey-07);
            }

            .headerText {
                color: var(--neutrals-grey-02, black);
                font-size: 16px;
                line-height: 24px;
                padding-top: 16px;
                padding-bottom: 16px;
                padding-left: 12px;
                margin: 0;
            }

            .planStatus {
                position: relative;
                border-radius: 10px;
                border: 1px solid var(--warning-yellow-7, yellow);
                color: var(--warning-yellow-7, yellow);
                font-size: 10px;
                line-height: 14px;
                padding-right: 5px;
                padding-left: 5px;
                padding-top: 1px;
                padding-bottom: 1px;
                margin-left: 10px;
                margin-top: 1px;
            }

            /* background with opacity */
            .planStatus::before {
                content: "";
                position: absolute;
                border-radius: inherit;
                top: 0;
                bottom: 0;
                right: 0;
                left: 0;
                background-color: currentColor;
                opacity: 0.07;
            }

            .planStatus[approved] {
                border: 1px solid var(--approved-plan-color, green);
                color: var(--approved-plan-color, green);
            }

            .header {
                border-bottom: 1px solid var(--neutrals-grey-07);
            }

            .statesContainer {
                display: flex;
                justify-content: center;
            }

            .states {
                display: grid;
                grid-template-columns: 105px 105px 105px;
                justify-content: center;
                margin-bottom: 15px;
                margin-top: 15px;
            }

            .state {
                position: relative;
                display: flex;
                flex-direction: column;
                align-items: center;
                color: var(--neutrals-grey-04, grey);
                font-size: 12px;
            }

            .state:first-child {
                margin-left: 0;
            }

            .state:not(:first-child):before {
                content: '';
                position: absolute;
                top: 7px;
                height: 2px;
                width: 100%;
                left: -50%;
                background-color: var(--neutrals-grey-07, grey);
            }

            .state[active]:before {
                background-color: var(--primary-blue-05, blue);
            }

            .state[active] {
                color: var(--primary-blue-05, blue);
            }

            .circle {
                width: 16px;
                height: 16px;
                border-radius: 8px;
                background-color: var(--neutrals-grey-07, grey);
                margin-bottom: 5px;
                z-index: 1;
            }

            .state[active]>.circle {
                background-color: var(--primary-blue-05, blue);
            }

            .footer {
                width: 100%;
                display: flex;
                justify-content: flex-end;
                align-items: center;
                border-top: 1px solid var(--neutrals-grey-07);
            }
            .footer.hasBasicPlan {
                flex-direction: column;
            }
            .footer.hasBasicPlan .footerButtonWrapper {
                display: flex;
                flex-direction: row;
            }       

            .footerButton {
                display: flex;
                justify-content: center;
                align-items: center;
                height: 32px;
                margin-top: 12px;
                margin-bottom: 12px;
                margin-right: 15px;
                min-width: 80px;
                font-size: 14px;
                font-family: MaerskText;
                padding: 0px 12px 0px 12px;
                background: none;
                border: none;
                outline: none;
                border-radius: 3px;
                color: var(--neutrals-grey-02);
                border: 1px solid var(--neutrals-grey-06, grey);
            }

            .footerButton[disabled] {
                filter: opacity(0.5);
                cursor: not-allowed;
            }

            .printButton svg {
                fill: currentColor;
                width: 16px;
                height: 16px;
                margin-right: 5px;
            }

            .action {
                background: var(--primary-blue-05, blue);
                color: var(--neutrals-grey-10, white);
            }

            .importButton,.exportButton{
                background-color: var(--primary-blue-900, blue);
                color: var(--neutrals-grey-10, white);
                font-family: MaerskText;
                font-size: 14px;
                font-style: normal;
                font-weight: 400;
                line-height: 20px;
                letter-spacing: 0em;
                text-align: left;
                border: 1px solid var(--primary-blue-900, blue);
            }
            .basicPlan {
                display: flex;
                flex-direction: row;
                align-items: center;
                justify-content: center;
                height: 100%;
                width: 100%;
                padding: 20px;
            }
            .basicPlan .text .basicText {
                color:#997303
            }
            .basicPlan .text .basicText p {
                margin: 0;
            }
            .footerButtonWrapper {
                display: flex;
            }    
        `];
    }

    render() {
        const planApproved = this._plan && this._plan.status === "approved";
        const hasBasicPlan = this._currentSchedule ? (this._currentSchedule.isBasicPlanAvailable || false ) : false;
        const hasBasicPlanClass = hasBasicPlan ? 'footer hasBasicPlan' : 'footer';
        return html`
        <voyage-popup ?shown=${this.shown} @close=${() => this._close()} ?loading=${ isVesselOnCloud ? this._loading : this.showLoader} no-close-button>
            <!-- Content popup -->
            <voyage-passage-plan-text-content .shown=${this._showPassagePlanTextContent} .content=${this._data} .fileTitle=${this._fileTitle} @close=${this._closeVoyTextContentPopup} @import=${this._updatePassagePlan}></voyage-passage-plan-text-content>

            <!-- Header -->
            <div class="header" slot="header">
                <div class="headerTextContainer">
                    <h1 class="headerText">Passage Plan</h1>
                    <div class="planStatus" ?approved=${planApproved}>${planApproved ? "APPROVED" : "PENDING"}</div>
                </div>
                <div class="statesContainer">
                    <div class="states">
                        <div class="state" active>
                            <div class="circle"></div>
                            <div>GENERAL</div>
                        </div>
                        <div class="state" ?active=${this.state !== "General"}>
                            <div class="circle"></div>
                            <div>VOYAGE</div>
                        </div>
                        <div class="state" ?active=${this.state === "Overview"}>
                            <div class="circle"></div>
                            <div>OVERVIEW</div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Content -->
            ${this._renderContent()}
           
            <!-- Footer -->
            <div class=${hasBasicPlanClass} slot="footer">
                ${
                    (isVesselOnCloud && hasBasicPlan ) ? html`
                    <div class="basicPlan">
                        <div class="text">
                            <div class ="basicText">
                                <p>Please note: You have submitted a Voyage basic plan for this voyage. Submissions via star connect cloud is no longer possible.</p>
                                <p>Please continue using Voyage basic until the completion of current voyage.</p>                
                            </div>
                        </div>
                        
                    </div>
                    `: html``
                }
                <div class="footerButtonWrapper">
                    ${this.state === "General" || this.state === "Voyage" ? html`
                    <button id="export" class="footerButton exportButton" @click=${this._exportTemplate}>Export texts</button>
                    <button id="import" class="footerButton importButton" @click=${this._importText}>Import texts</button>
                    ` : html``}
                    <button id="print" class="footerButton printButton" @click=${() => this._printPDF()}>${SvgIcons.solidPrint}Print Passage Plan</button>
                    <button id="previous" class="footerButton" @click=${() => this._previous()} ?disabled=${this.state === 'General'}>Previous</button>
                    <button id="next" class="footerButton" @click=${() => this._next()} ?disabled=${this.state === 'Overview' || this._cannotContinue}>Next</button>
                    <button id="save" class="footerButton" @click=${this._save} ?disabled=${this._cannotContinue}>Save</button>
                    <button id="saveAndClose" class="footerButton action" @click=${() => this._saveAndClose(planApproved ?? false)}>${(planApproved && !this._showSaveAsDraftButton) ? "Close" : "Save and Close"}</button>
                </div
            </div>
        </voyage-popup>
        `;
    }

    private _renderContent () {
        const showPassagePlanFromPlan:boolean  = (this.showInFloatPanel === "schedule" || this.showInFloatPanel === "viewRoute");

        switch (this.state) {
            case "General":
                return html`<voyage-passage-plan-general @plan-validation=${(evt: CustomEvent) => { this._cannotContinue = evt.detail.isValid }} id="content" .isOfflinePlan=${this._offlinePlan ? true : false} .plan=${this._offlinePlan ? this._offlinePlan : this._plan} .readOnly=${this.readOnly}  ?showPassagePlanFromPlan = ${Boolean(showPassagePlanFromPlan)} ></voyage-passage-plan-general>`;

            case "Voyage":
                return html`<voyage-passage-plan-voyage id="content" .isOfflinePlan=${this._offlinePlan ? true : false} .plan=${this._offlinePlan ? this._offlinePlan : this._plan} .readOnly=${this.readOnly} ?showPassagePlanFromPlan = ${showPassagePlanFromPlan}></voyage-passage-plan-voyage>`;

            case "Overview":
                return html`
                  <voyage-passage-plan-overview 
                    id="content"
                    .isOfflinePlan=${(!!this._offlinePlan)} 
                    @close=${this._handleClosePopup} 
                    .plan=${this._offlinePlan ? this._offlinePlan : this._plan} 
                    ?showPassagePlanFromPlan=${showPassagePlanFromPlan}
                    .closePassagePlaneModal=${this._close}
                    .toggleFuelEfficientModal=${this.toggleFuelEfficientModal}
                  >
                  </voyage-passage-plan-overview>
                `;
        }
    }

    _handleClosePopup(){
        this._saveAndClose(false);
    }
    
    updated(changedProperties: PropertyValues) {
        changedProperties.forEach((oldValue, propName) => {
            switch (propName) {
                case "shown":
                case "scheduleId":
                case "readOnly":
                    if (this.shown) {
                        this.state = "General";
                        this._setPlanFromScheduleId();
                    }
                    break;
                case "nonHydraCalculatedRoute":
                    this.state = "General";
                    if(!(isVesselOnCloud && !isOnline())) {
                        this._setOfflinePlan();
                    }
                    break;
            }
        });
    }

    connectedCallback() {
        super.connectedCallback();
        VesselPlanningModel!.addEventListener(SuggestedRouteEvents.PlanChanged, <EventListener>this._onPlanUpdated)
        VesselPlanningModel!.addEventListener('local-passage-plan-updated',this._setShowSaveAsDraftButton)
        VesselPlanningModel!.addEventListener(AlignBPREvent.ClosePassagePlan, this._close)
        if (isVesselOnCloud) {
            VoyageImoModel!.addEventListener('updating-plan-event', this._showLoaderFn)
            VoyageImoModel!.addEventListener('updating-plan-event-completed', this._hideLoader)
            VoyageImoModel!.addEventListener('updating-plan-event-failed', this._hideLoader)
        } else {
            VesselPlanningModel!.addEventListener(PassagePlanEvents.UpdatingPlan, () => { this.showLoader = true })
            VesselPlanningModel!.addEventListener(PassagePlanEvents.UpdatingPlanCompleted, () => { this.showLoader = false })
        }

    }

    disconnectedCallback() {
        super.disconnectedCallback();
        VesselPlanningModel!.removeEventListener(SuggestedRouteEvents.PlanChanged, <EventListener>this._onPlanUpdated)
        VesselPlanningModel!.removeEventListener('local-passage-plan-updated',this._setShowSaveAsDraftButton)
        VesselPlanningModel!.removeEventListener(AlignBPREvent.ClosePassagePlan, this._close)
        if(isVesselOnCloud) {
            VoyageImoModel!.removeEventListener('updating-plan-event', this._showLoaderFn)
            VoyageImoModel!.removeEventListener('updating-plan-event-completed', this._hideLoader)
            VoyageImoModel!.removeEventListener('updating-plan-event-failed', this._hideLoader)
        } else {
            VesselPlanningModel!.addEventListener(PassagePlanEvents.UpdatingPlan, () => { this.showLoader = true })
            VesselPlanningModel!.addEventListener(PassagePlanEvents.UpdatingPlanCompleted, () => { this.showLoader = false })
        }
    }

    private _showLoaderFn() {
        this._loading = isOnline();    
    }

    private _hideLoader() {
        this._loading = false;
    }

    private _setShowSaveAsDraftButton() {
        this._showSaveAsDraftButton = true;
        this.requestUpdate();
    }

    private _onPlanUpdated(evt: CustomEvent<{ scheduleId: string }>) {
        if (this._plan && this.scheduleId === evt.detail.scheduleId) {
            this._setPlanFromScheduleId();
        }
    }
    async _setPlanFromScheduleId() {
        if (this._offlinePlan) {
            return;
        }
        
        if (!this.scheduleId) {
            this._plan = null;
            return;
        }

        if (this.readOnly) {
            // Use latest approved plan in this case
            this._plan = await VesselPlanningModel!.getApprovedPlan(this.scheduleId);
        }
        else {
            this._plan = await VesselPlanningModel!.getCurrentPlan(this.scheduleId);
        }

        const currentSchedule = await IndexedDBService.schedules.get(this.scheduleId);
        if (currentSchedule) {
            this._currentSchedule = currentSchedule;          
        }
    }

    private async _close() {
        this.dispatchEvent(new CustomEvent('close', { detail: { passagePlanExitView: this.state } }));
        //TODO: should update currentSelectedRouteId's route's passage plan
    }


    private async _saveAndClose(planApproved: boolean) {
        if (!planApproved || (planApproved && this._showSaveAsDraftButton)) {
            VesselPlanningModel!._savePassagePlan();
        }
       this.dispatchEvent(new CustomEvent('close', { detail: { passagePlanExitView: this.state } }));
       const currentPlan = await VesselPlanningModel!.getCurrentPlan(this.scheduleId);
       if(currentPlan && currentPlan.status === "approved"){
           const approvedRoute = currentPlan.routes?.[currentPlan.selectedRouteIndex];
            if(approvedRoute.routeCreatedBy == "WR"){
                const toastAlreadyShown =
                this._shownToasts.has(currentPlan.scheduleId)
                if (!toastAlreadyShown) {
                    this._shownToasts.add(currentPlan.scheduleId);
                    VoyageToastManager.createToast({ message: WRMessageTypes.Accept, type: 'success' });
                }
            }else {
                //if WR exist in route and we dismissed it earlier
                const wrRejected = JSON.parse(localStorage.getItem('wrRejected') || '{}');

                if(wrRejected && wrRejected.scheduleId === currentPlan.scheduleId && approvedRoute?.weatherRoutingInfo?.status === 'reject'){ 
                    VoyageToastManager.createToast({ message: WRMessageTypes.Reject, type: 'success' });
                    localStorage.removeItem('wrRejected');
                }
            }
       }
    }

    private async _save() {
        VesselPlanningModel!._savePassagePlan();
    }

    private async _next() {
        VesselPlanningModel!._savePassagePlan();
        const popup = this.shadowRoot!.querySelector("voyage-popup");

        if (this.state === "General") {
            this.state = "Voyage";
        }
        else {
            this.state = "Overview";
        }

        await this.updateComplete;

        if (popup) {
            popup.scrollTo(0, 0);
        }

        this.requestUpdate();
    }

    private async _previous() {
        VesselPlanningModel!._savePassagePlan();
        const popup = this.shadowRoot!.querySelector("voyage-popup");

        if (this.state === "Voyage") {
            this.state = "General";
        }
        else {
            this.state = "Voyage";
        }

        await this.updateComplete;
        if (popup) {
            popup.scrollTo(0, 0);
        }
    }

    private async _printPDF() {
        const plan = this._offlinePlan || this._plan;
        const isOfflinePlan = this._offlinePlan !== null;
        const showPassagePlanFromPlan:boolean  = (this.showInFloatPanel === "schedule" || this.showInFloatPanel === "viewRoute");  

        if (plan) {
            const pdf = new VoyagePassagePlanPdf(plan);
            await pdf.generatePdf(isOfflinePlan,showPassagePlanFromPlan);
            pdf.save();
        }

    }

    private _closeVoyTextContentPopup() {
        this._showPassagePlanTextContent = false;
    }

    private _updatePassagePlan(e: CustomEvent) {
        const showPassagePlanFromPlan  = (this.showInFloatPanel === "schedule" || this.showInFloatPanel === "viewRoute"); 
        const values: Content[] = e.detail.values;

        if (!this._plan) return;
        const currentSelectedRouteIndex = this._plan.routes.findIndex(route => route.routeId === this._plan?.currentSelectedRouteId);
        const { passagePlan } = showPassagePlanFromPlan ? this._plan.routes[this._plan.selectedRouteIndex]: this._plan.routes[currentSelectedRouteIndex];

        if (!passagePlan) return;
        const isKey = (id: string, obj: RoutePassagePlanProperties):
            id is keyof Omit<RoutePassagePlanProperties, 'notEcdisCompliant' | 'safetyMessages' | 'weatherInfo' | 'equipmentStatus' | 'weatherRouting'> => {
            return typeof obj[id as keyof RoutePassagePlanProperties] === 'string';
        }
        
        values.forEach(el => {
            switch (el.id) {
                case "info":
                    passagePlan.weatherInfo.info = el.value;
                    break;
                case "failures":
                    passagePlan.equipmentStatus.failures = el.value;
                    break;
                case "operationalCondition":
                    passagePlan.equipmentStatus.operationalCondition = el.value;
                    break;
                default:
                    const { id } = el;
                    if (isKey(id, passagePlan)) {
                        passagePlan[id] = el.value;
                    }
                    break;
            }
        });

        VesselPlanningModel!.setPlan(this._plan.scheduleId, this._plan, false);
    }

    private _exportTemplate() {
        if (!this._plan) return;
        const currentSelectedRouteIndex = this._plan.routes.findIndex(route => route.routeId === this._plan?.currentSelectedRouteId);
        const { passagePlan } = currentSelectedRouteIndex > -1 ? this._plan.routes[currentSelectedRouteIndex] : this._plan.routes[this._plan.selectedRouteIndex];
        if (!passagePlan) return;

        const value = templateData.map(el => {
            let info;
            switch (el.id) {
                case "info":
                    info = passagePlan.weatherInfo.info;
                    break;
                case "failures":
                    info = passagePlan.equipmentStatus.failures;
                    break;
                case "operationalCondition":
                    info = passagePlan.equipmentStatus.operationalCondition;
                    break;
                default:
                    info = DataExtractor.getDataFromPlan(el.id, this._plan);
                    break;
            }
            
            return {
                id: el.id,
                title: el.title,
                value: info,
                selected: info !== '' ? true : false
            }
        });

        const templateName = `Template for Passage plan ${this._plan?.departureTerminalCode}-${this._plan?.arrivalTerminalCode} ${new Date(Date.now()).toISOString()}`;
        let dataStr = JSON.stringify(value);
        let dataUri = 'data:application/json;charset=utf-8,' + encodeURIComponent(dataStr);
        let exportFileDefaultName = `${templateName}.template`;
        let linkElement = document.createElement('a');
        linkElement.setAttribute('href', dataUri);
        linkElement.setAttribute('download', exportFileDefaultName);
        linkElement.click();
    }

    private _importText() {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = ".template";
        input.click();

        const handleFileLoad = (event: { target: { result: any; } | null }) => {
            const result = event.target?.result;
            const res = JSON.parse(result);

            if (res.length > 0) {
                this._data = res;
                this._showPassagePlanTextContent = true;
            }
        }

        const handleSelect = (e: Event) => {
            const reader = new FileReader();
            reader.onload = handleFileLoad;
            const target = e.target as HTMLInputElement;
            const file: File = (target.files as FileList)[0];

            if (file) {
                reader.readAsText(file);
                this._fileTitle = file.name;
            }
        }
        input.addEventListener('change', handleSelect, false);
    }

    /**
    * @name setOfflinePlan
    * @description Creating a plan with passageplan and a route
    * that has not been successfully calculated by Hydra.
    * 
    * Due to missing Hydra calculation, the Passage Plan must not be
    * approved. So to enable still filling out and printing Passage Plan,
    * the Passage Plan with a failed route will be in 'offline mode'.
    * 
    * Offline Mode means that plan will not be sent to backend.
    * Only HTTP request is for getting Vessel Characteristics which enables
    * some calculations for the passage plan
    */
    private async _setOfflinePlan() {
        if (!this.nonHydraCalculatedRoute) {
            this._offlinePlan = null;
            return;
        }

        const currentPlan = await VesselPlanningModel!.getCurrentPlan(this.scheduleId);
        if (!currentPlan) {
            return;
        }
        const offlinePlan = deepCopy(currentPlan);
        offlinePlan.routes = [this.nonHydraCalculatedRoute];
        offlinePlan.selectedRouteIndex = 0;

        this._offlinePlan = offlinePlan;
        this._vesselCharacteristics = await VesselPlanningModel!.getVesselCharacteristics();
        DataExtractor.addCalculatedValues(this._offlinePlan, this._vesselCharacteristics);
    }
}

